import { useCallback, useRef } from 'react';
import { useDownloadFiles } from '@/talons';
import { useDbFunctions } from '@/talons';
import { getOnStorage, setOnStorage } from '@/shared';
import { Tables, tableKeys } from '@/types';

interface LoginDataSyncHook {
  /**
   * Executa sincronização otimizada durante o login
   * Deve ser chamado apenas uma vez após login bem-sucedido
   */
  syncDataOnLogin: () => Promise<boolean>;
  
  /**
   * Indica se a sincronização está em andamento
   */
  isSyncing: boolean;
}

/**
 * Hook otimizado para sincronização de dados apenas durante o login
 * Executado uma única vez por sessão para máxima performance
 */
export const useLoginDataSync = (): LoginDataSyncHook => {
  const { getLinks } = useDownloadFiles();
  const { getOnDB } = useDbFunctions();
  
  const isSyncingRef = useRef(false);

  // Tabelas críticas que devem estar sempre disponíveis
  const criticalTables: Tables[] = [
    'cliente',
    'produtos', 
    'preco',
    'categoria',
    'marcas'
  ];

  /**
   * Verifica rapidamente se as tabelas críticas estão disponíveis
   */
  const quickValidateCriticalTables = useCallback(async (): Promise<boolean> => {
    try {
      for (const table of criticalTables) {
        const data = await getOnDB(table);
        
        if (!data) return false;
        
        if (Array.isArray(data) && data.length === 0) return false;
        if (typeof data === 'object' && Object.keys(data).length === 0) return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro na validação rápida:', error);
      return false;
    }
  }, [getOnDB, criticalTables]);

  /**
   * Executa sincronização otimizada durante o login - REUTILIZA LÓGICA EXISTENTE
   */
  const syncDataOnLogin = useCallback(async (): Promise<boolean> => {
    if (isSyncingRef.current) {
      console.log('useLoginDataSync: Sincronização já em andamento');
      return false;
    }

    const startTime = Date.now();
    
    try {
      isSyncingRef.current = true;
      
      console.log('useLoginDataSync: Iniciando sincronização de login...');

      // 1. Verificação rápida das tabelas críticas
      console.log('useLoginDataSync: Verificando tabelas críticas...');
      const hasCriticalData = await quickValidateCriticalTables();
      
      if (hasCriticalData) {
        // 2. Se dados críticos existem, verifica se precisa de atualização
        const lastSync = getOnStorage('last_data_sync');
        const loginDate = getOnStorage('login_date');
        
        if (lastSync && loginDate) {
          const lastSyncTime = new Date(lastSync).getTime();
          const loginTime = new Date(loginDate).getTime();
          
          // Se sincronização foi feita após o login, não precisa sincronizar
          if (lastSyncTime > loginTime) {
            console.log('useLoginDataSync: Dados já sincronizados para este login - PULANDO');
            sessionStorage.setItem('data_validated_this_session', 'true');
            return true;
          }
        }
        
        // 3. Verifica se dados são suficientemente recentes (menos de 6 horas)
        if (lastSync) {
          const lastSyncTime = new Date(lastSync).getTime();
          const now = Date.now();
          const sixHours = 6 * 60 * 60 * 1000;
          
          if ((now - lastSyncTime) < sixHours) {
            console.log('useLoginDataSync: Dados recentes (< 6h) - PULANDO sincronização');
            sessionStorage.setItem('data_validated_this_session', 'true');
            return true;
          }
        }
      }

      // 4. REUTILIZA A LÓGICA EXISTENTE - getLinks() do useDownloadFiles
      console.log('useLoginDataSync: Executando sincronização usando lógica existente...');
      const syncResult = await getLinks({ isBackground: false });
      
      if (syncResult) {
        // 5. Atualiza timestamp e marca sessão como validada
        setOnStorage('last_data_sync', new Date().toISOString());
        sessionStorage.setItem('data_validated_this_session', 'true');
        
        const endTime = Date.now();
        const duration = (endTime - startTime) / 1000;
        
        console.log(`useLoginDataSync: Sincronização concluída com sucesso em ${duration}s`);
        return true;
      }
      
      console.error('useLoginDataSync: Falha na sincronização');
      return false;
      
    } catch (error) {
      console.error('useLoginDataSync: Erro durante sincronização:', error);
      return false;
    } finally {
      isSyncingRef.current = false;
    }
  }, [getLinks, quickValidateCriticalTables]);

  return {
    syncDataOnLogin,
    isSyncing: isSyncingRef.current
  };
};